import { emptyApi } from "./emptyApi";
import { Task1120TrackerApiResponse } from "types/Models/TaskTracker";
import { stringify } from "qs";

export const FORM_1120_REVIEW_STATUS = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export type Form1120ReviewStatus =
  (typeof FORM_1120_REVIEW_STATUS)[keyof typeof FORM_1120_REVIEW_STATUS];

export type Form1120WorkflowStep =
  | "DOCUMENT_INGESTION"
  | "M1_MAPPING"
  | "REPORT_GENERATION";

export type Form1120ReviewLog = {
  timestamp: string;
  level: "INFO" | "WARN" | "ERROR";
  message: string;
};

export type ReviewIssue = {
  issue: string;
  observation: string;
  impact: string;
  action: string;
};

export type ReviewSection = {
  area: string;
  observation: string;
  risk: string;
  action: string;
};

export type ReviewDataQuality = {
  issue: string;
  details: string;
  action: string;
};

export type ReviewOverallAssessment = {
  risk_level: "Low" | "Medium" | "High";
  main_concerns: string;
  recommended_next_steps: string | string[];
};

export type ReviewNotes = {
  title: string;
  key_issues: ReviewIssue[];
  financial_review: ReviewSection[];
  compliance_operational_review: ReviewSection[];
  data_quality_integrity_checks: ReviewDataQuality[];
  key_questions_for_reviewer: string[];
  overall_assessment: ReviewOverallAssessment;
};

export type Form1120ReviewResult = {
  review_notes: ReviewNotes;
};

export type Form1120FileRef = {
  uuid: string;
  name: string;
};

export type Form1120Review = {
  uuid: string;
  task_uuid: string;
  entity_name: string;
  status: Form1120ReviewStatus;
  deep_validation: boolean;
  current_focus: string | null;
  current_step: Form1120WorkflowStep | null;
  completed_steps: Form1120WorkflowStep[];
  logs: Form1120ReviewLog[];
  result: Form1120ReviewResult | null;
  error_message: string | null;
  created_at: string;
};

export type CreateForm1120ReviewPayload = {
  task_uuid: string;
  entity_name: string;
  group_uuid: string;
  deep_validation: boolean;
  file_refs: Form1120FileRef[];
  additional_prompt?: string;
};

export const task1120TrackerApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    createForm1120Review: build.mutation<
      { review_id: string; status: Form1120ReviewStatus },
      CreateForm1120ReviewPayload
    >({
      query: (payload) => ({
        url: "/api/inkle/tasks/v1/form-1120-review/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["FORM_1120_REVIEW"],
    }),

    getForm1120BasePrompt: build.query<{ prompt: string }, void>({
      query: () => ({ url: "/api/inkle/tasks/v1/form-1120-review/prompt/" }),
    }),

    getForm1120Review: build.query<
      Form1120Review | null,
      { review_id?: string; task_uuid?: string }
    >({
      query: ({ review_id, task_uuid }) => {
        const params = review_id
          ? `?review_id=${review_id}`
          : `?task_uuid=${task_uuid}`;
        return { url: `/api/inkle/tasks/v1/form-1120-review/${params}` };
      },
      providesTags: ["FORM_1120_REVIEW"],
    }),

    getTask1120Tracker: build.query<
      Task1120TrackerApiResponse,
      {
        page_num: number;
        page_size?: number | null;
        season: number;
        search_term?: string | null;
        assigned_agents_uuids?: string | null;
        service_team_uuids?: string | null;
        tax_preparer_uuids?: string | null;
        start_pickup_date?: string | null;
        end_pickup_date?: string | null;
        start_etd_date?: string | null;
        end_etd_date?: string | null;
        task_1120_tracker_global_status?: string | null;
        entity_uuids?: string | null;
        form_1120_bookkeeping_status?: string | null;
        bookkeeper_uuids?: string | null;
        form_7004_status?: string | null;
        form_1120_status?: string | null;
        form_5471_status?: string | null;
        form_5472_status?: string | null;
        form_r_and_d_status?: string | null;
        state_registrations_uuids?: string | null;
        previous_season_form_1120_status?: string | null;
        previous_season_form_5471_status?: string | null;
        previous_season_form_5472_status?: string | null;
        tax_year_end: string;
        payment_tab_filter: string;
        formation_type?: string | null;
        entity_taxed_as?: string | null;
        tag_filter?: string | null;
        generate_report?: boolean | null;
        export_to_mail?: boolean | null;
        fbar_status?: string | null;
        etd_type?: string | null;
        reviewer_uuids?: string | null;
        preparer_assigned_start_date?: string | null;
        preparer_assigned_end_date?: string | null;
        reviewer_assigned_start_date?: string | null;
        reviewer_assigned_end_date?: string | null;
        form_1120_completed_start_date?: string | null;
        form_1120_completed_end_date?: string | null;
        is_urgent?: boolean | null;
        is_amendment?: boolean | null;
      }
    >({
      query: (params) => {
        const queryUrl = stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/v1/provider/form-1120-tracker/${queryUrl}`,
        };
      },
      providesTags: ["TASK_1120_TRACKER", "NOTES"],
    }),

    setUrgentFlag: build.mutation<void, { task_id: string; reason: string }>({
      query: ({ task_id, reason }) => ({
        url: `/api/inkle/tasks/v1/${task_id}/urgent/`,
        method: "PUT",
        body: { reason },
      }),
      invalidatesTags: ["TASK_1120_TRACKER"],
    }),

    removeUrgentFlag: build.mutation<void, { task_id: string }>({
      query: ({ task_id }) => ({
        url: `/api/inkle/tasks/v1/${task_id}/urgent/`,
        method: "DELETE",
      }),
      invalidatesTags: ["TASK_1120_TRACKER"],
    }),

    getGlobalStatusFilterOptions: build.query<
      { display: string; value: string }[],
      void
    >({
      query: () =>
        "/api/inkle/tasks/v1/provider/form-1120-tracker/global-status-options/",
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTask1120TrackerQuery,
  useLazyGetTask1120TrackerQuery,
  useCreateForm1120ReviewMutation,
  useGetForm1120ReviewQuery,
  useGetForm1120BasePromptQuery,
  useSetUrgentFlagMutation,
  useRemoveUrgentFlagMutation,
  useGetGlobalStatusFilterOptionsQuery,
} = task1120TrackerApis;
